import React, { useState, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Heart, MessageCircle, Upload, MoreVertical, Trash2, Download, Wand2, Play, Pause, Film, Clock } from 'lucide-react'
import { Video, Scene } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'

interface VideoGalleryProps {
  videos: Video[]
  selectedVideoId: string | null
  selectedScene: Scene | undefined
  viewMode: 'grid' | 'list'
  filterType: 'all' | 'main' | 'favorites' | 'generated' | 'uploaded' | 'comments'
  sortOrder: 'desc' | 'asc'
  thumbnailScale?: number
  onVideoSelect: (videoId: string, showComments?: boolean) => void
  onUploadClick: () => void
  onGenerateClick: () => void
}

const VideoGallery: React.FC<VideoGalleryProps> = ({
  videos,
  selectedVideoId,
  selectedScene,
  viewMode,
  filterType,
  sortOrder,
  thumbnailScale = 1,
  onVideoSelect,
  onUploadClick,
  onGenerateClick
}) => {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const [hoveredVideoId, setHoveredVideoId] = useState<string | null>(null)
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null)
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())
  
  const { setMainVideo, toggleVideoFavorite, deleteVideo, loading } = useProjectStore()
  const { user } = useAuthStore()

  // Filter and sort videos
  const filteredAndSortedVideos = useMemo(() => {
    let filtered = [...videos]

    // Apply filters
    switch (filterType) {
      case 'main':
        filtered = filtered.filter(vid => vid.isMain)
        break
      case 'favorites':
        filtered = filtered.filter(vid => vid.isFavorite)
        break
      case 'generated':
        filtered = filtered.filter(vid => vid.name.startsWith('ai_'))
        break
      case 'uploaded':
        filtered = filtered.filter(vid => !vid.name.startsWith('ai_'))
        break
      case 'comments':
        filtered = filtered.filter(vid => vid.comments.length > 0)
        filtered.sort((a, b) => b.comments.length - a.comments.length)
        break
    }

    // Apply sort by date (if not comments filter which has its own sort)
    if (filterType !== 'comments') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.uploadedAt).getTime()
        const dateB = new Date(b.uploadedAt).getTime()
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
      })
    }

    return filtered
  }, [videos, filterType, sortOrder])

  const handleMenuClick = (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation()
    setActiveMenuId(activeMenuId === videoId ? null : videoId)
  }

  const handleSetMainVideo = async (videoId: string) => {
    if (!selectedScene) return
    try {
      await setMainVideo(videoId, selectedScene.id)
      setActiveMenuId(null)
    } catch (error) {
      console.error('Failed to set main video:', error)
    }
  }

  const handleToggleFavorite = async (videoId: string) => {
    try {
      await toggleVideoFavorite(videoId)
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDeleteVideo = async (videoId: string) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await deleteVideo(videoId)
        setActiveMenuId(null)
      } catch (error) {
        console.error('Failed to delete video:', error)
      }
    }
  }

  const handlePlayPause = (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation()
    const videoEl = videoRefs.current.get(videoId)
    
    if (playingVideoId === videoId) {
      videoEl?.pause()
      setPlayingVideoId(null)
    } else {
      // Pause any currently playing video
      if (playingVideoId) {
        videoRefs.current.get(playingVideoId)?.pause()
      }
      videoEl?.play()
      setPlayingVideoId(videoId)
    }
  }

  const getCommentCount = (video: Video) => {
    return video.comments.reduce((total, comment) => total + 1 + comment.replies.length, 0)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!selectedScene) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Film className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Scene Selected</h3>
          <p className="text-white/70">
            Select a scene from the sidebar to view and manage videos
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Gallery Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {videos.length > 0 ? (
          <div 
            className={viewMode === 'grid' ? 'grid gap-4' : 'space-y-4'}
            style={viewMode === 'grid' ? {
              gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(200 * thumbnailScale)}px, 1fr))`
            } : undefined}
          >
            <AnimatePresence>
              {/* Combined Upload & Generate Placeholder */}
              <motion.div
                key="placeholder-combined"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="group relative glass-card overflow-hidden border-2 border-dashed border-white/20"
              >
                <div className="aspect-w-16 aspect-h-9 bg-gray-800">
                  <div className="absolute inset-0 flex flex-col">
                    {/* Upload Section - Top Half */}
                    <button
                      onClick={onUploadClick}
                      className="flex-1 flex items-center justify-center bg-cyan-500/10 hover:bg-cyan-500/15 border-b border-dashed border-cyan-500/30 transition-colors"
                    >
                      <div className="text-center">
                        <Upload className="w-10 h-10 text-cyan-400/60 mx-auto mb-2" />
                        <p className="text-white/70 font-medium text-sm">Upload Video</p>
                      </div>
                    </button>
                    
                    {/* Generate Section - Bottom Half */}
                    <button
                      onClick={onGenerateClick}
                      className="flex-1 flex items-center justify-center bg-purple-500/10 hover:bg-purple-500/15 transition-colors"
                    >
                      <div className="text-center">
                        <Wand2 className="w-10 h-10 text-purple-400/60 mx-auto mb-2" />
                        <p className="text-white/70 font-medium text-sm">Generate with AI</p>
                      </div>
                    </button>
                  </div>
                </div>
              </motion.div>

              {filteredAndSortedVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className={`group relative cursor-pointer ${
                    viewMode === 'grid' 
                      ? 'glass-card overflow-hidden hover:scale-105 transition-transform duration-200 p-0'
                      : 'glass-card flex items-center space-x-4 p-4'
                  } ${
                    selectedVideoId === video.id ? 'ring-2 ring-cyan-400' : ''
                  }`}
                  onClick={() => onVideoSelect(video.id)}
                  onMouseEnter={() => setHoveredVideoId(video.id)}
                  onMouseLeave={() => setHoveredVideoId(null)}
                >
                  {viewMode === 'grid' ? (
                    <>
                      {/* Grid View - Video Preview */}
                      <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
                        {/* Video Element */}
                        <video
                          ref={(el) => el && videoRefs.current.set(video.id, el)}
                          src={video.url}
                          poster={video.thumbnailUrl}
                          className="absolute inset-0 w-full h-full object-cover"
                          muted
                          loop
                          playsInline
                          onEnded={() => setPlayingVideoId(null)}
                        />
                        
                        {/* Play/Pause Overlay */}
                        <div 
                          className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                            hoveredVideoId === video.id || playingVideoId === video.id ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          <button
                            onClick={(e) => handlePlayPause(e, video.id)}
                            className="glass-button p-4 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                          >
                            {playingVideoId === video.id ? (
                              <Pause className="w-8 h-8 text-white" />
                            ) : (
                              <Play className="w-8 h-8 text-white" />
                            )}
                          </button>
                        </div>

                        {/* Duration Badge */}
                        {video.metadata?.duration > 0 && (
                          <div className="absolute bottom-2 right-2 glass-button px-2 py-1 text-xs text-white bg-black/60 flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDuration(video.metadata.duration)}</span>
                          </div>
                        )}
                      </div>

                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        {/* Top indicators */}
                        <div className="absolute top-2 left-2 flex flex-col space-y-1 pointer-events-auto">
                          <div className="flex space-x-1">
                            {video.isMain && (
                              <span className="glass-button px-2 py-1 text-xs text-yellow-300 bg-yellow-500/20">
                                Main
                              </span>
                            )}
                            {video.isFavorite && (
                              <span className="glass-button px-2 py-1 text-xs text-red-300 bg-red-500/20">
                                Favorite
                              </span>
                            )}
                          </div>
                          
                          {/* AI/Upload tag */}
                          {video.name.startsWith('ai_') ? (
                            <span className="glass-button px-2 py-1 text-xs text-purple-300 bg-purple-500/40 flex items-center space-x-1 shadow-lg">
                              <Wand2 className="w-3 h-3" />
                              <span>AI</span>
                            </span>
                          ) : (
                            <span className="glass-button px-2 py-1 text-xs text-cyan-300 bg-cyan-500/40 flex items-center space-x-1 shadow-lg">
                              <Upload className="w-3 h-3" />
                              <span>Uploaded</span>
                            </span>
                          )}
                        </div>

                        {/* Menu button */}
                        <button
                          onClick={(e) => handleMenuClick(e, video.id)}
                          className="absolute top-2 right-2 glass-button p-1.5 hover:bg-white/30 pointer-events-auto"
                        >
                          <MoreVertical className="w-4 h-4 text-white" />
                        </button>

                        {/* Bottom actions */}
                        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between pointer-events-auto">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSetMainVideo(video.id)
                              }}
                              className={`glass-button p-1.5 ${
                                video.isMain ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                              }`}
                              disabled={loading}
                            >
                              <Star className="w-4 h-4" fill={video.isMain ? 'currentColor' : 'none'} />
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleToggleFavorite(video.id)
                              }}
                              className={`glass-button p-1.5 ${
                                video.isFavorite ? 'text-red-400' : 'text-white hover:text-red-400'
                              }`}
                              disabled={loading}
                            >
                              <Heart className="w-4 h-4" fill={video.isFavorite ? 'currentColor' : 'none'} />
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onVideoSelect(video.id, true)
                              }}
                              className="glass-button p-1.5 text-white hover:text-blue-400 flex items-center space-x-1"
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-xs">{getCommentCount(video)}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* List View */}
                      <div className="w-20 h-12 flex-shrink-0 bg-gray-800 rounded overflow-hidden relative">
                        <video
                          src={video.url}
                          poster={video.thumbnailUrl}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="w-4 h-4 text-white" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{video.name}</h3>
                        <p className="text-white/60 text-sm">
                          {new Date(video.uploadedAt).toLocaleDateString()}
                          {video.metadata?.duration > 0 && ` • ${formatDuration(video.metadata.duration)}`}
                        </p>
                      </div>

                      <div className="flex items-center space-x-2">
                        {video.isMain && (
                          <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
                        )}
                        {video.isFavorite && (
                          <Heart className="w-4 h-4 text-red-400" fill="currentColor" />
                        )}
                        <div className="flex items-center space-x-1 text-white/60">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-sm">{getCommentCount(video)}</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => handleMenuClick(e, video.id)}
                        className="glass-button p-1.5 hover:bg-white/20"
                      >
                        <MoreVertical className="w-4 h-4 text-white" />
                      </button>
                    </>
                  )}

                  {/* Dropdown Menu */}
                  {activeMenuId === video.id && (
                    <motion.div
                      className="absolute right-0 w-44 glass-card border border-white/20 z-50 shadow-xl"
                      style={{ top: '-14px', marginRight: '8px' }}
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className="py-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSetMainVideo(video.id)
                          }}
                          disabled={loading || video.isMain}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Star className="w-3.5 h-3.5" />
                          <span className="whitespace-nowrap">{video.isMain ? 'Main' : 'Set as Main'}</span>
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleFavorite(video.id)
                          }}
                          disabled={loading}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Heart className="w-3.5 h-3.5" />
                          <span className="whitespace-nowrap">{video.isFavorite ? 'Remove Favorite' : 'Add Favorite'}</span>
                        </button>

                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            const link = document.createElement('a')
                            link.href = video.url
                            link.download = video.name
                            link.target = '_blank'
                            link.click()
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download</span>
                        </button>

                        <hr className="border-white/10 my-1" />
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteVideo(video.id)
                          }}
                          disabled={loading}
                          className="w-full px-3 py-1.5 text-left text-sm text-red-300 hover:bg-red-500/20 flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-2xl">
              <div className="flex justify-center space-x-4 mb-8">
                <Upload className="w-16 h-16 text-cyan-400/40" />
                <Wand2 className="w-16 h-16 text-purple-400/40" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No Videos Yet</h3>
              <p className="text-white/70 mb-8">
                Upload your first video or generate one with AI to start building this scene
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={onUploadClick}
                  className="glass-button bg-cyan-500/30 hover:bg-cyan-500/40 text-white px-6 py-3 flex items-center space-x-2"
                >
                  <Upload className="w-5 h-5" />
                  <span>Upload Videos</span>
                </button>
                <button
                  onClick={onGenerateClick}
                  className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-6 py-3 flex items-center space-x-2"
                >
                  <Wand2 className="w-5 h-5" />
                  <span>Generate with AI</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Close menu when clicking outside */}
      {activeMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setActiveMenuId(null)}
        />
      )}
    </div>
  )
}

export default VideoGallery

